import LegalSection, { LegalSection as LegalSectionType } from '@/components/ui/LegalSection';
import { createFileRoute } from '@tanstack/react-router';
import { FileText } from 'lucide-react';
import { useEffect } from 'react';

export const Route = createFileRoute('/_public/swim-sign-tos')({
  component: TermsPage,
})

export default function TermsPage() {
  useEffect(() => {
    document.title = 'Terms of Service | Swim Sign';
  }, []);

  const sections: LegalSectionType[] = [
    {
      title: "",
      content: [
        "This Electronic Signature Consent and Disclosure (\"Consent\") describes your rights and responsibilities when using the electronic signature features (\"E-Signature Services\") provided by Swim Homes (\"Swim\", \"we\", \"us\", or \"our\"). Please read this Consent carefully before using our E-Signature Services."
      ]
    },
    {
      title: "1. Consent to Electronic Signatures and Records",
      content: [
        "By clicking \"I Agree\" or using our E-Signature Services, you expressly consent to:",
        "Creating and using electronic signatures through our platform",
        "Receiving documents and disclosures electronically rather than in paper form",
        "Having your electronic signature be legally binding, with the same force and effect as a handwritten signature",
        "Creating legally binding records of your transactions",
        "This consent applies to all documents you sign or receive through our E-Signature Services, including but not limited to real estate listing agreements, offers to purchase, counteroffers, amendments, disclosures, and other transaction-related documents."
      ]
    },
    {
      title: "2. Legal Effect of Electronic Signatures",
      content: [
        "Under Canadian law, including the Personal Information Protection and Electronic Documents Act (PIPEDA) and provincial electronic commerce legislation, electronic signatures are legally valid and enforceable when certain requirements are met. When you use our E-Signature Services in accordance with this Consent, your electronic signatures are intended to satisfy these legal requirements.",
        "By using our E-Signature Services, you acknowledge and agree that:",
        "Your electronic signature constitutes your legal signature",
        "Documents you electronically sign are legally binding",
        "You cannot later contest the validity of a document solely because it was signed electronically"
      ]
    },
    {
      title: "3. \"Adopt and Sign\" Process",
      content: [
        "When using our \"Adopt and Sign\" feature, you will:",
        "Create your signature by one of the following methods:",
        "Typing your name and selecting a signature style",
        "Drawing your signature using your mouse, touchpad, or finger on a touchscreen",
        "Uploading an image of your handwritten signature",
        "Review the document before signing",
        "Adopt your signature by clicking the \"Adopt and Sign\" button, indicating your intent to sign the document",
        "Complete the signing process by applying your signature to the document",
        "By clicking \"Adopt and Sign,\" you confirm that:",
        "You have reviewed and agree to the content of the document",
        "You intend to sign the document electronically",
        "You are the person authorized to sign the document",
        "You understand that once applied, your signature cannot be unilaterally revoked"
      ]
    },
    {
      title: "4. Signature Adoption Legal Intent",
      content: [
        "Each time you use the \"Adopt and Sign\" feature, you are indicating your legal intent to sign the document electronically. Your electronic signature created through this feature will be legally binding with the same force and effect as a handwritten signature.",
        "By using this feature, you acknowledge and agree that:",
        "You are the person authorized to sign the document",
        "You have reviewed the entire document before signing",
        "You understand the content and legal implications of the document",
        "You intend your electronic signature to be legally binding",
        "You are not under duress or signing the document under false pretenses",
        "The signature you create accurately represents your legal signature",
        "You will not contest the validity of your signature solely because it was created electronically"
      ]
    },
    {
      title: "5. Authentication and Verification",
      content: [
        "When you use our E-Signature Services, we record the following information to authenticate your signature:",
        "Date and time of signature",
        "IP address of the device used",
        "Email address associated with your account",
        "Any authentication methods used (email, access code, etc.)",
        "Device information and browser type",
        "This information creates an audit trail that may be used to verify the authenticity of your signature if it is later questioned.",
        "Our E-Signature Services use various methods to authenticate signers, which may include:",
        "Email verification",
        "Access codes",
        "SMS verification",
        "Other authentication methods we may implement",
        "You agree that these authentication methods are commercially reasonable and sufficient for verifying your identity within the context of our services."
      ]
    },
    {
      title: "6. Document Integrity and Tamper Protection",
      content: [
        "Swim Homes implements cryptographic measures, including a Tamper-Evident Seal, to detect if a document has been altered after signing. When a document is fully executed, we:",
        "Generate a unique digital \"fingerprint\" (hash) of the document",
        "Encrypt this hash using cryptographic keys",
        "Apply a Tamper-Evident Seal to the document",
        "If anyone attempts to modify the document after the seal is applied, the system will detect this discrepancy during verification. For more information about our Tamper-Evident Seal technology, please visit our Help Center.",
        "Despite these protections, you are responsible for:",
        "Reviewing the document before signing",
        "Downloading and saving a copy of the signed document",
        "Reporting any discrepancies or issues immediately"
      ]
    },
    {
      title: "7. Hardware and Software Requirements",
      content: [
        "To use our E-Signature Services, you need:",
        "An email account",
        "A device with internet access (computer, tablet, or smartphone)",
        "A current web browser with cookies and JavaScript enabled",
        "A PDF viewer",
        "Sufficient storage space to save documents (if you wish to download them)",
        "We will notify you if these requirements change in a way that materially impacts your ability to use our E-Signature Services."
      ]
    },
    {
      title: "8. Obtaining Paper Copies",
      content: [
        "After signing a document, you will have the opportunity to download, print, and save a copy for your records. We strongly recommend that you save or print a copy of all important documents.",
        "If you need a paper copy of a document you signed or received through our E-Signature Services, you may:",
        "Print the document yourself from the digital copy",
        "Request a paper copy by contacting us at support@swimhomes.ca (additional fees may apply)"
      ]
    },
    {
      title: "9. Withdrawing Consent",
      content: [
        "You may withdraw your consent to use electronic signatures and receive documents electronically at any time by contacting us at support@swimhomes.ca. However, withdrawing your consent:",
        "Will only apply to future transactions and not to documents already signed",
        "May prevent you from using our E-Signature Services for future transactions",
        "Will not invalidate any previously signed documents"
      ]
    },
    {
      title: "10. Updating Your Contact Information",
      content: [
        "You are responsible for keeping your email address current. To update your email address or other contact information, please update your account profile or contact us at support@swimhomes.ca.",
        "If you fail to update your contact information and miss an important document or notice as a result, you understand that this will not invalidate any agreement or transaction."
      ]
    },
    {
      title: "11. Multiple Document Consent",
      content: [
        "This Consent applies to all documents sent or received through our E-Signature Services. You will not receive a separate electronic disclosure consent for each document, unless required by law."
      ]
    },
    {
      title: "12. Revocation Limitations",
      content: [
        "Once you have electronically signed a document using our E-Signature Services, you cannot unilaterally revoke or remove your signature. If you need to revoke your signature after signing, you must:",
        "Contact all other parties to the document",
        "Follow any termination or revocation procedures specified in the document itself",
        "Create a new document explicitly revoking the signed document (if necessary)"
      ]
    },
    {
      title: "13. Security Risks and Responsibilities",
      content: [
        "While we implement security measures to protect our E-Signature Services, electronic systems carry inherent security risks. You acknowledge these risks and agree to:",
        "Maintain the confidentiality of your account credentials",
        "Sign out of your account when using shared devices",
        "Use secure networks when accessing our E-Signature Services",
        "Promptly report any suspected unauthorized access to your account",
        "Not allow others to sign documents on your behalf"
      ]
    },
    {
      title: "14. Document Retention",
      content: [
        "We will retain electronically signed documents and related records for at least seven (7) years, or longer if required by law. However, you should download and retain copies of important documents for your own records."
      ]
    },
    {
      title: "15. Certificate of Completion",
      content: [
        "Each completed document includes a Certificate of Completion that contains:",
        "Names and email addresses of all signers",
        "Timestamps showing when each party viewed and signed the document",
        "IP addresses used during the signing process",
        "Authentication methods used by each signer",
        "The document's unique identifier and hash value",
        "This Certificate of Completion is attached to the signed document and serves as an additional verification record."
      ]
    },
    {
      title: "16. No Guarantee of Legal Validity",
      content: [
        "While Swim Homes implements measures to ensure the legal validity of electronic signatures created through our platform, we cannot guarantee that all electronic signatures will be legally valid in all contexts or jurisdictions. The legal validity of an electronic signature may depend on:",
        "The type of document being signed",
        "The specific legal requirements applicable to the transaction",
        "The authentication methods used",
        "The intentions of the signing parties",
        "The laws of the relevant jurisdiction",
        "If you have concerns about the legal validity of electronic signatures for your specific transaction, we recommend consulting with a legal professional."
      ]
    },
    {
      title: "17. Legal Compliance Statement",
      content: [
        "Our E-Signature Services are designed to comply with:",
        "Personal Information Protection and Electronic Documents Act (PIPEDA) of Canada",
        "Electronic Commerce Act of Ontario",
        "Similar electronic transaction laws in other Canadian provinces",
        "However, we cannot guarantee that electronic signatures will be legally valid in all contexts or for all document types. You are responsible for determining whether electronic signatures are appropriate for your specific needs."
      ]
    },
    {
      title: "18. Acknowledgment",
      content: [
        "By using our E-Signature Services, you acknowledge that you:",
        "Have read and understand this Consent",
        "Consent to use electronic signatures and receive electronic documents",
        "Can access and read documents in electronic format",
        "Have the hardware and software required to use our E-Signature Services",
        "If you do not agree to these terms, you should not use our E-Signature Services.",
        "For questions about this Consent or our E-Signature Services, please contact us at support@swimhomes.ca."
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="grow py-12">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <FileText className="h-7 w-7 text-primary" />
            <h1 className="text-3xl font-bold">Terms of Service</h1>
          </div>

          <div className="mb-8">
            <p className="text-muted-foreground mb-2">Last updated: March 03, 2025</p>

            <div className="mt-6 prose prose-lg max-w-none">
              <p>
                {sections[0].content && sections[0].content[0]}
              </p>
              <p>
                {sections[0].content && sections[0].content[1]}
              </p>
            </div>
          </div>

          <div className="space-y-10 mt-12">
            {sections.filter(section => section.title).map((section, index) => (
              <div key={index} className="prose max-w-none">
                <LegalSection section={section} />
              </div>
            ))}
          </div>

          <div className="mt-16 border-t pt-6 mb-12">
            <p className="text-muted-foreground">
              If you have any questions about our Terms of Service, please contact us at{' '}
              <a href="mailto:support@swimhomes.ca" className="text-primary hover:underline">
                support@swimhomes.ca
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}